import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import ShiftPicker from 'modules/work_schedules/components/ShiftPicker'
import { DayConfigurationWithBreaksForm } from 'modules/work_schedules/hooks/useDayConfigurations'
import type DayConfigurationForm from 'modules/work_schedules/lib/forms/DayConfigurationForm'

import AddFieldButton from './AddFieldButton'
import WorkScheduleBreakInputs from './WorkScheduleBreakInputs'

export interface Props {
  day: string
  weekday: string
  dayConfigForms: DayConfigurationWithBreaksForm[]
  onAddDayConfig: (weekday: string) => void
  onDeleteDayConfig: (dayConfigForm: DayConfigurationForm) => void
  onBlur: (dayConfigForm: DayConfigurationForm) => void
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    dayConfiguration: DayConfigurationWithBreaksForm
  ) => void
  onDeleteBreak: (dayConfiguration: DayConfigurationWithBreaksForm, form: PlannedBreakForm) => void
  canAddBreaks: boolean
}

const FixedDayConfigurationInput = ({
  day,
  weekday,
  dayConfigForms,
  onAddDayConfig,
  onDeleteDayConfig,
  onBlur,
  onAddBreak,
  onDeleteBreak,
  canAddBreaks,
}: Props) => {
  const isValidForm = dayConfigForms.every(({ dayConfiguration, breaks }) => {
    const { isValid, formattedStartAt: startAt, formattedEndAt: endAt } = dayConfiguration

    return (
      isValid &&
      breaks.every((form) => {
        return form.isValid({ startAt, endAt })
      })
    )
  })

  const color = isValidForm ? 'viridian1000' : 'grey400'

  return (
    <Box
      border={{
        all: {
          color: color,
          width: 's2',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
      padding="s16"
      background="grey000White"
    >
      <Box flexDirection="column" gap="s24">
        {dayConfigForms.map((form, index) => {
          const dayConfigForm = form.dayConfiguration
          return (
            <Box flexDirection="row" key={index} gap="s8">
              <Box gap="s8" justifyContent="spaceBetween" flexGrow>
                <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
                  {index === 0 ? (
                    <Box paddingTop="s8">
                      <Text.Normal color={'grey1000'} weight="medium">
                        {day}
                      </Text.Normal>
                    </Box>
                  ) : (
                    <Box width="s32"></Box>
                  )}

                  <Box width="s360">
                    <ShiftPicker
                      form={dayConfigForm}
                      isNotValid={dayConfigForm.isDirty && !dayConfigForm.isValid}
                      isOvernight={dayConfigForm.isOvernight}
                      onBlur={() => onBlur(dayConfigForm)}
                      clearInput={dayConfigForm.clearInput}
                      onDelete={() => onDeleteDayConfig(dayConfigForm)}
                    />
                  </Box>
                </Box>

                {!!form.breaks.length && (
                  <Box gap="s8">
                    {form.breaks.map((breakForm, i) => (
                      <WorkScheduleBreakInputs
                        key={i}
                        form={form}
                        breakForm={breakForm}
                        validationParams={{
                          startAt: dayConfigForm.formattedStartAt,
                          endAt: dayConfigForm.formattedEndAt,
                        }}
                        onDeleteBreak={onDeleteBreak}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box paddingTop="s6">
                {canAddBreaks ? (
                  <AddFieldButton
                    onAddShift={() => onAddDayConfig(weekday)}
                    onAddBreak={(breakConfiguration, breakType) => {
                      onAddBreak(breakConfiguration, breakType, form)
                    }}
                  />
                ) : (
                  <Button type="secondary" onClick={() => onAddDayConfig(weekday)} iconName="add" />
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default observer(FixedDayConfigurationInput)
